import { Query, QueryClient, QueryKey } from "@tanstack/react-query";
import { isEqual } from "lodash";

/**
 * Keeps object URL query data leak-free by subscribing once per QueryClient and query-key prefix.
 *
 * Why this exists:
 * - Some queries cache object URL strings produced via URL.createObjectURL(...).
 * - Replacing cached URL strings without revoking previous values leaks browser memory.
 * - Query removal should revoke the final URL value as well.
 *
 * What it does:
 * - Tracks the latest object URL per query hash.
 * - On "updated": revokes the previous URL when it changes.
 * - On "removed": revokes the last known URL.
 * - Warns when matched query data is not string | null | undefined.
 *
 * Notes:
 * - Uses a global registry to remain stable across HMR in development.
 * - `subscriptionKey` namespaces independent cleanup domains.
 */
type ObjectUrlCleanupSubscriptionState = {
  subscribedClients: WeakSet<QueryClient>;
  objectUrlByQueryHash: Map<string, string>;
};

type EnsureObjectUrlQueryCleanupSubscriptionParams = {
  queryClient: QueryClient;
  queryKeyPrefix: QueryKey;
  subscriptionKey: string;
};

const globalObjectUrlCleanupStateKey = "__ranaObjectUrlCleanupSubscriptionStates__";

const getGlobalObjectUrlCleanupStates = () => {
  // Persist across hot-reloads so we do not duplicate subscriptions in dev.
  const globalState = globalThis as typeof globalThis & {
    [globalObjectUrlCleanupStateKey]?: Map<string, ObjectUrlCleanupSubscriptionState>;
  };

  if (!globalState[globalObjectUrlCleanupStateKey]) {
    globalState[globalObjectUrlCleanupStateKey] = new Map();
  }

  return globalState[globalObjectUrlCleanupStateKey];
};

const getObjectUrlCleanupState = (subscriptionKey: string) => {
  const states = getGlobalObjectUrlCleanupStates();
  const existing = states.get(subscriptionKey);
  if (existing) return existing;

  const created: ObjectUrlCleanupSubscriptionState = {
    subscribedClients: new WeakSet<QueryClient>(),
    objectUrlByQueryHash: new Map<string, string>(),
  };

  states.set(subscriptionKey, created);
  return created;
};

const getObjectUrlOrWarn = (data: unknown, subscriptionKey: string) => {
  if (typeof data === "string") {
    return data;
  }

  if (data === null || data === undefined) {
    return null;
  }

  console.warn(
    `[${subscriptionKey}] Expected query data to be a string object URL, null, or undefined, but received:`,
    data
  );
  return null;
};

const syncObjectUrlForQuery = (
  query: Query,
  objectUrlByQueryHash: Map<string, string>,
  subscriptionKey: string
) => {
  const currentObjectUrl = getObjectUrlOrWarn(query.state.data, subscriptionKey);
  if (currentObjectUrl) {
    objectUrlByQueryHash.set(query.queryHash, currentObjectUrl);
  } else {
    objectUrlByQueryHash.delete(query.queryHash);
  }

  return currentObjectUrl;
};

export const ensureObjectUrlCleanup = ({
  queryClient,
  queryKeyPrefix,
  subscriptionKey,
}: EnsureObjectUrlQueryCleanupSubscriptionParams) => {
  const { subscribedClients, objectUrlByQueryHash } = getObjectUrlCleanupState(subscriptionKey);

  if (subscribedClients.has(queryClient)) return;
  subscribedClients.add(queryClient);

  for (const query of queryClient.getQueryCache().findAll({ queryKey: queryKeyPrefix })) {
    syncObjectUrlForQuery(query, objectUrlByQueryHash, subscriptionKey);
  }

  queryClient.getQueryCache().subscribe((event) => {
    // Compare only the prefix length so trailing key segments (e.g. IDs) can vary.
    if (!isEqual(event.query.queryKey.slice(0, queryKeyPrefix.length), queryKeyPrefix)) return;

    const queryHash = event.query.queryHash;
    const previousObjectUrl = objectUrlByQueryHash.get(queryHash);
    const currentObjectUrl = syncObjectUrlForQuery(
      event.query,
      objectUrlByQueryHash,
      subscriptionKey
    );

    if (event.type === "updated" && previousObjectUrl && previousObjectUrl !== currentObjectUrl) {
      URL.revokeObjectURL(previousObjectUrl);
    }

    if (event.type === "removed") {
      const objectUrlToRevoke = previousObjectUrl ?? currentObjectUrl;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
      objectUrlByQueryHash.delete(queryHash);
    }
  });
};
